"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link, Element as ScrollElement } from "react-scroll";
import { cn } from "@/utils";
import {
  getSiteNavbarScrollOffset,
  SCROLL_SPY_FALLBACK_OFFSET,
  SCROLL_SPY_TOP_PADDING,
} from "@/lib/navbar-scroll";

export type ScrollSpyItem<T = unknown> = {
  id: string;
  label: string;
  data?: T;
};

export type ScrollSpyLayoutClassNames = {
  root?: string;
  nav?: string;
  navTitle?: string;
  navItem?: string;
  activeNavItem?: string;
  content?: string;
  section?: string;
};

export function formatScrollSpyNavIndex(index: number): string {
  return `${String(index + 1).padStart(2, "0")}.`;
}

export type ScrollSpyLayoutProps<T = unknown> = {
  items: ScrollSpyItem<T>[];
  renderNavItem: (item: ScrollSpyItem<T>, index: number) => React.ReactNode;
  renderSection: (item: ScrollSpyItem<T>, index: number) => React.ReactNode;
  /** Extra classes for a single section wrapper, e.g. to tighten spacing after a short section. */
  getSectionClassName?: (
    item: ScrollSpyItem<T>,
    index: number
  ) => string | undefined;
  contentPrefix?: React.ReactNode;
  navTitle?: string | false;
  onNavItemClick?: (item: ScrollSpyItem<T>, index: number) => void;
  scrollOffset?: number;
  scrollDuration?: number;
  classNames?: ScrollSpyLayoutClassNames;
};

const DEFAULTS: Required<Omit<ScrollSpyLayoutClassNames, never>> = {
  root: "scroll-spy-sidebar-layout",
  nav: "lg:sticky top-20 z-10 self-start",
  navTitle: "mb-4 text-sm font-medium text-muted-foreground-light",
  navItem:
    "flex items-center gap-2 border-b py-4 text-base text-muted-foreground-light capitalize cursor-pointer transition-[color,box-shadow] hover:text-white",
  activeNavItem: "text-white glow-border-b glow-border-b-toc glow-secondary",
  content: "space-y-16 min-w-0",
  section: "w-full overflow-hidden min-w-0 space-y-6",
};

/** Extra px so sections activate when react-scroll lands slightly below the spy line. */
const SCROLL_SPY_ACTIVATION_SLOP = 48;

function getSectionScrollProgress(
  items: ScrollSpyItem[],
  activeId: string,
  activationLine: number
): number {
  const activeIndex = items.findIndex((item) => item.id === activeId);
  if (activeIndex === -1) return 0;

  const element = document.getElementById(activeId);
  if (!element) return 0;

  const isLastSection = activeIndex === items.length - 1;

  if (isLastSection) {
    const sectionStartScrollY =
      window.scrollY + element.getBoundingClientRect().top - activationLine;
    const maxScrollY =
      document.documentElement.scrollHeight - window.innerHeight;
    const span = maxScrollY - sectionStartScrollY;
    if (span <= 0) return 1;

    const progress = (window.scrollY - sectionStartScrollY) / span;
    return Math.min(1, Math.max(0, progress));
  }

  const sectionStart = element.getBoundingClientRect().top;
  const nextElement = document.getElementById(items[activeIndex + 1].id);
  if (!nextElement) return 0;

  const sectionEnd = nextElement.getBoundingClientRect().top;
  const span = sectionEnd - sectionStart;
  if (span <= 0) return 1;

  const progress = (activationLine - sectionStart) / span;
  return Math.min(1, Math.max(0, progress));
}

function useSiteNavbarScrollOffset(extraPadding = SCROLL_SPY_TOP_PADDING) {
  // Keep SSR and the first client render identical; measure the navbar after mount.
  const [offset, setOffset] = useState(SCROLL_SPY_FALLBACK_OFFSET);

  useEffect(() => {
    const updateOffset = () => {
      setOffset(getSiteNavbarScrollOffset(extraPadding));
    };

    updateOffset();

    const navbar = document.getElementById("site-navbar");
    if (!navbar) return;

    const observer = new ResizeObserver(updateOffset);
    observer.observe(navbar);
    window.addEventListener("scroll", updateOffset, { passive: true });
    window.addEventListener("resize", updateOffset);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", updateOffset);
      window.removeEventListener("resize", updateOffset);
    };
  }, [extraPadding]);

  return offset;
}

export default function ScrollSpyLayout<T = unknown>({
  items,
  renderNavItem,
  renderSection,
  getSectionClassName,
  contentPrefix,
  navTitle = "Table of Contents",
  onNavItemClick,
  scrollOffset,
  scrollDuration = 500,
  classNames,
}: ScrollSpyLayoutProps<T>) {
  const navbarOffset = useSiteNavbarScrollOffset();
  const resolvedOffset = scrollOffset ?? navbarOffset;
  const activeNavItemClass =
    classNames?.activeNavItem ?? DEFAULTS.activeNavItem;
  const [activeId, setActiveId] = useState(items[0]?.id);
  const [sectionProgress, setSectionProgress] = useState(0);
  const pendingActiveIdRef = useRef<string | null>(null);
  const pendingTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    setActiveId(items[0]?.id);
    setSectionProgress(0);
    pendingActiveIdRef.current = null;
    if (pendingTimeoutRef.current !== null) {
      window.clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = null;
    }
  }, [items]);

  useEffect(() => {
    if (!items.length) return;

    const threshold = resolvedOffset < 0 ? -resolvedOffset : resolvedOffset;
    const activationLine = threshold + SCROLL_SPY_ACTIVATION_SLOP;

    const updateActiveId = () => {
      const resolvedActiveId = pendingActiveIdRef.current;

      if (resolvedActiveId) {
        setActiveId((current) =>
          current === resolvedActiveId ? current : resolvedActiveId
        );
        setSectionProgress(
          getSectionScrollProgress(items, resolvedActiveId, activationLine)
        );
        return;
      }

      const firstId = items[0]?.id;
      if (!firstId) return;

      let nextActiveId = firstId;

      for (let i = 0; i < items.length; i++) {
        const element = document.getElementById(items[i].id);
        if (element && element.getBoundingClientRect().top <= activationLine) {
          nextActiveId = items[i].id;
          continue;
        }
        break;
      }

      const nextProgress = getSectionScrollProgress(
        items,
        nextActiveId,
        activationLine
      );

      setActiveId((current) =>
        current === nextActiveId ? current : nextActiveId
      );
      setSectionProgress((current) =>
        current === nextProgress ? current : nextProgress
      );
    };

    updateActiveId();
    window.addEventListener("scroll", updateActiveId, { passive: true });
    window.addEventListener("resize", updateActiveId);

    return () => {
      window.removeEventListener("scroll", updateActiveId);
      window.removeEventListener("resize", updateActiveId);
    };
  }, [items, resolvedOffset]);

  const handleNavItemClick = (item: ScrollSpyItem<T>, index: number) => {
    if (pendingTimeoutRef.current !== null) {
      window.clearTimeout(pendingTimeoutRef.current);
    }

    pendingActiveIdRef.current = item.id;
    setActiveId(item.id);
    setSectionProgress(0);
    onNavItemClick?.(item, index);

    pendingTimeoutRef.current = window.setTimeout(() => {
      pendingActiveIdRef.current = null;
      pendingTimeoutRef.current = null;
      setActiveId(item.id);
    }, scrollDuration + 100);
  };

  useEffect(() => {
    return () => {
      if (pendingTimeoutRef.current !== null) {
        window.clearTimeout(pendingTimeoutRef.current);
      }
    };
  }, []);

  if (!items?.length) return null;

  /** Align in-document anchors with fixed header when users open #fragment links without JS smooth-scroll. */
  const sectionScrollMarginStyle =
    resolvedOffset < 0
      ? ({ scrollMarginTop: `${-resolvedOffset}px` } as React.CSSProperties)
      : undefined;

  return (
    <div className={cn(classNames?.root ?? DEFAULTS.root)}>
      <div className={cn(classNames?.nav ?? DEFAULTS.nav)}>
        {navTitle !== false && (
          <p className={cn("!font-semibold mt-1", classNames?.navTitle ?? DEFAULTS.navTitle)}>
            {navTitle}
          </p>
        )}
        {items.map((item, index) => (
          <div className="flex flex-col" key={item.id}>
            <Link
              to={item.id}
              href={`#${encodeURIComponent(item.id)}`}
              smooth={true}
              duration={scrollDuration}
              offset={resolvedOffset}
              onClickCapture={() => {
                handleNavItemClick(item, index);
              }}
              className={cn(
                classNames?.navItem ?? DEFAULTS.navItem,
                activeId === item.id && activeNavItemClass
              )}
              style={
                activeId === item.id
                  ? ({
                      "--section-progress": sectionProgress,
                    } as React.CSSProperties)
                  : undefined
              }
            >
              {renderNavItem(item, index)}
            </Link>
          </div>
        ))}
      </div>
      <div className={cn(classNames?.content ?? DEFAULTS.content)}>
        {contentPrefix}
        {items.map((item, index) => (
          <ScrollElement
            key={item.id}
            name={item.id}
            id={item.id}
            style={sectionScrollMarginStyle}
            className={cn(
              classNames?.section ?? DEFAULTS.section,
              getSectionClassName?.(item, index)
            )}
          >
            {renderSection(item, index)}
          </ScrollElement>
        ))}
      </div>
    </div>
  );
}
